import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Search, Edit2, FileText, AlertCircle } from 'lucide-react';
import API_BASE_URL  from '../../../config/baseApi';
import InputField from 'components/fields/InputField';
import SelectField from 'components/fields/SelectField';

export default function Items() {
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    itemCode: '',
    itemName: '',
    price: '',
    category: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEntries, setShowEntries] = useState('10');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const categories = [
    { value: 'R', label: 'Restaurant', display: 'Restaurant' },
    { value: 'O', label: 'Other', display: 'Other' }
  ];

  const categoryColors = {
    'R': 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    'O': 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
  };

  // Fetch items on component mount
  useEffect(() => {
    fetchItems();
  }, []);

  // Fetch all items
  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/items`);
      const data = await response.json();
      
      if (data.success) {
        setItems(data.data);
      } else {
        setError(data.message || 'Failed to fetch items');
      }
    } catch (err) {
      setError('Failed to connect to the server');
      console.error('Error fetching items:', err);
    } finally {
      setLoading(false);
    }
  };

    const getCategoryDisplay = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.display : category;
  };
  const filteredItems = useMemo(() => {
    return items.filter(item =>
      item.item_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCategoryDisplay(item.item_cat).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.price && item.price.toString().includes(searchTerm))
    );
  }, [items, searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!formData.itemCode.trim() || !formData.itemName.trim() || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate price for Restaurant category
    if (formData.category === 'R' && !formData.price) {
      alert('Price is required for Restaurant items');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = editingId 
        ? `${API_BASE_URL}/api/items/${editingId}`
        : `${API_BASE_URL}/api/items`;
      
      const method = editingId ? 'PUT' : 'POST';

      const requestBody = {
        item_code: formData.itemCode,
        item_name: formData.itemName,
        item_cat: formData.category
      };

      // Only include price for Restaurant items
      if (formData.category === 'R') {
        requestBody.price = parseFloat(formData.price);
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        await fetchItems(); // Refresh the list
        setFormData({ itemCode: '', itemName: '', price: '', category: '' });
        setEditingId(null);
        alert(data.message);
      } else {
        setError(data.message || 'Operation failed');
        if (data.errors) {
          const errorMessages = Object.values(data.errors).flat().join(', ');
          alert(errorMessages);
        }
      }
    } catch (err) {
      setError('Failed to save item');
      console.error('Error saving item:', err);
      alert('Failed to save item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
    setFormData({
      itemCode: item.item_code,
      itemName: item.item_name,
      price: item.price || '',
      category: item.item_cat
    });
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setFormData({ itemCode: '', itemName: '', price: '', category: '' });
    setEditingId(null);
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/items/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        await fetchItems(); // Refresh the list
        alert(data.message);
      } else {
        setError(data.message || 'Failed to delete item');
        alert(data.message || 'Failed to delete item');
      }
    } catch (err) {
      setError('Failed to delete item');
      console.error('Error deleting item:', err);
      alert('Failed to delete item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    return categoryColors[category] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  };


  // const InputField = ({ label, name, type = "text", value, onChange, placeholder, required = false }) => (
  //   <div className="flex flex-col">
  //     <label className="text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
  //       {label} {required && <span className="text-red-500">*</span>}
  //     </label>
  //     <input
  //       type={type}
  //       name={name}
  //       value={value}
  //       onChange={onChange}
  //       placeholder={placeholder}
  //       className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
  //     />
  //   </div>
  // );

  // const SelectField = ({ label, name, value, onChange, options, required = false }) => (
  //   <div className="flex flex-col">
  //     <label className="text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
  //       {label} {required && <span className="text-red-500">*</span>}
  //     </label>
  //     <select
  //       name={name}
  //       value={value}
  //       onChange={onChange}
  //       className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer transition-all"
  //     >
  //       <option value="">Select {label}</option>
  //       {options.map((opt) => (
  //         <option key={opt.value} value={opt.value}>
  //           {opt.label}
  //         </option>
  //       ))}
  //     </select>
  //   </div>
  // );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Items Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Add and manage hotel menu items</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">Error</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              ×
            </button>
          </div>
        )}

        <div className="space-y-6">
          {/* Add/Edit Item Form */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Plus className="w-6 h-6 text-blue-500" />
              {editingId ? 'Edit Item' : 'Add New Item'}
            </h2>
            <form onSubmit={handleAddItem} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <InputField
                  label="Item Code"
                  name="itemCode"
                  type="text"
                  value={formData.itemCode}
                  onChange={handleInputChange}
                  placeholder="e.g., MENU001"
                  required
                />
                <InputField
                  label="Item Name"
                  name="itemName"
                  type="text"
                  value={formData.itemName}
                  onChange={handleInputChange}
                  placeholder="e.g., Biryani"
                  required
                />
                <InputField
                  label="Price"
                  name="price"
                  type="number"
                  value={formData.price === null ? '' : formData.price}
                  onChange={handleInputChange}
                  placeholder="e.g., 350"
                  required={formData.category === 'R'}
                />
                <SelectField
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  options={categories}
                  required
                />
                <div className="flex items-end gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? 'Processing...' : editingId ? 'Update' : 'Add Item'}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2.5 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {formData.category === 'R' && (
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  * Price is required for Restaurant items
                </p>
              )}
            </form>
          </div>

          {/* Items Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Table Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Items List</h2>
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 dark:text-gray-400">Show</span>
                    <select
                      value={showEntries}
                      onChange={(e) => setShowEntries(e.target.value)}
                      className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                    <span className="text-sm text-gray-700 dark:text-gray-400">entries</span>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Item Code</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Item Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-600 dark:text-gray-400">
                        <div className="flex flex-col items-center justify-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-3"></div>
                          <p className="text-sm">Loading items...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-600 dark:text-gray-400">
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
                          <p className="text-sm">No items found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 font-medium">{index + 1}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 font-medium">{item.item_code}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">{item.item_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 font-semibold">
                          {item.price ? `₨${parseFloat(item.price).toFixed(2)}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(item.item_cat)}`}>
                            {getCategoryDisplay(item.item_cat)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm space-x-2 flex">
                          <button 
                            onClick={() => handleEditItem(item)}
                            className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            disabled={loading}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            disabled={loading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing {filteredItems.length > 0 ? 1 : 0} to {Math.min(filteredItems.length, parseInt(showEntries))} of {filteredItems.length} entries
              </p>
              <div className="flex gap-2">
                <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                  Prev
                </button>
                <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Copyright © 2026. All right reserved.
          </p>
        </div>
      </div>
    </div>
  );
}